import React from 'react'

import {
  ClockIcon,
  ClipboardDocumentListIcon,
  ChartPieIcon,
} from '@heroicons/react/24/outline'
import Link from '../components/Link'

const Home: React.FC = () => {
  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <main className="flex-1 overflow-auto min-h-0">
        <section className="flex flex-col md:flex-row md:items-center md:gap-8 lg:gap-12 py-12 px-4 md:p-12 border-b-2">
          <div className="left flex flex-col md:w-3/5 mb-8">
            <h1 className="text-left text-4xl font-extrabold text-gray-900">
              Streamline student IEP data with TrackIEP.
            </h1>
            <h1 className="mb-4 italic text-left text-3xl font-extrabold text-gray-700">
              Simple. Secure. Centralized.
            </h1>
            <p className="mb-8 text-left">
              TrackIEP empowers educators to deliver, monitor, and report
              student accommodations and goals with ease, all in one centralized
              location.
            </p>
            <Link
              variant="primary"
              to="/request-demo"
              className="group md:max-w-xs md:mx-auto relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Request a Demo
            </Link>
          </div>
          <div className="right md:w-2/5">
            <img
              src="https://doj8kefen1eet.cloudfront.net/images/marketing-accommodations.gif"
              alt="Accommodations gif"
              className="mt-8 md:mt-0 mx-auto rounded-md shadow-xl"
              style={{
                marginLeft: '-2px',
              }}
            />
          </div>
        </section>

        <section className="bg-white py-16 px-4 md:py-20 md:px-12">
          <div className="max-w-5xl mx-auto">
            <div
              className="relative w-full"
              style={{ paddingBottom: '56.25%' }}
            >
              <iframe
                className="absolute top-0 left-0 w-full h-full rounded-lg shadow-2xl"
                src="https://player.vimeo.com/video/1187163207?title=0&byline=0&portrait=0"
                frameBorder="0"
                allow="autoplay; fullscreen; picture-in-picture"
                allowFullScreen
                title="TrackIEP - Legal Precedent"
              />
            </div>

            <div className="text-center mt-8">
              <Link
                variant="primary"
                to="/videos"
                className="group md:max-w-xs md:mx-auto relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                More Videos
              </Link>
            </div>
          </div>
        </section>

        <section className="bg-blue-100 py-12 px-4 md:p-12 flex flex-col gap-8 justify-center items-center">
          <div>
            <h1 className="mt-8 text-left md:text-center text-2xl font-bold text-gray-900 max-w-4xl">
              TrackIEP was created with the belief that every educator deserves
              the tools to deliver IEP accommodations and goals with
              transparency, accountability, and care.
            </h1>
          </div>
          <div className="my-8 flex flex-col md:flex-row gap-6 max-w-6xl mx-auto">
            <div className="card flex-1 bg-white rounded-lg shadow-md p-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                Save Time
              </h3>
              <p className="text-gray-600">
                No more spreadsheets. No more time wasted searching for data.
                With our centralized tracking system, everything is where you
                need it to be.
              </p>
              <ClockIcon className="w-12 h-12 text-blue-600 my-8 mx-auto" />
            </div>
            <div className="card flex-1 bg-white rounded-lg shadow-md p-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                Improve Compliance
              </h3>
              <p className="text-gray-600">
                Transparent. Accountable. Easily view student IEP data by
                school, class, student, educator, and/or time period. No more
                surprises.
              </p>
              <ClipboardDocumentListIcon className="w-12 h-12 text-blue-600 my-8 mx-auto" />
            </div>
            <div className="card flex-1 bg-white rounded-lg shadow-md p-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                Create Compelling Data
              </h3>
              <p className="text-gray-600">
                Support your school community by generating accommodation and
                goal progress reports that keep all stakeholders informed and
                guide IEP conversations.
              </p>
              <ChartPieIcon className="w-12 h-12 text-blue-600 my-8 mx-auto" />
            </div>
          </div>
        </section>

        <section className="bg-blue-500 py-12 px-4 md:p-12">
          <div className="flex flex-col md:flex-row md:items-center md:gap-8 lg:gap-12">
            <div className="md:w-2/5">
              <img
                src="https://doj8kefen1eet.cloudfront.net/images/marketing-accommodations-log.gif"
                alt="Screenshot"
                className="mx-auto rounded-md shadow-xl"
              />
            </div>
            <div className="flex flex-col md:w-3/5 mb-8 md:mb-0">
              <h1 className="my-6 text-3xl text-left font-semibold text-white max-w-4xl">
                Streamline how your educators document, monitor, and report IEP
                accommodations
              </h1>
              <ul className="list-disc list-inside text-left font-semibold text-white mb-6 max-w-4xl">
                <li>Log daily accommodations for students</li>
                <li>Generate compelling data reports</li>
                <li>Stay compliant with federal mandates</li>
              </ul>
              <Link
                variant="secondary"
                to="/request-demo"
                className="group md:max-w-xs relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-blue-600 bg-white hover:bg-gray-100 hover:text-blue-600 focus:outline-none focus:ring-2 focus:ring-offset-2 mr-auto mb-6"
              >
                Request a Demo
              </Link>
            </div>
          </div>
        </section>

        <section className="bg-gray-100 py-12 px-4 md:p-12">
          <div className="flex flex-col md:flex-row md:items-center md:gap-8 lg:gap-12">
            <div className="flex flex-col md:w-3/5 mb-8 md:mb-0">
              <h1 className="my-6 text-3xl text-left font-semibold text-gray-900 max-w-4xl">
                Track student goal data and generate progress reports to share
                with stakeholders
              </h1>
              <ul className="list-disc list-inside text-left font-semibold text-gray-900 mb-6 max-w-4xl">
                <li>Create unique goals for each student</li>
                <li>Generate progress reports</li>
                <li>Discover patterns and insights</li>
              </ul>
              <Link
                variant="primary"
                to="/request-demo"
                className="group md:max-w-xs relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 mr-auto mb-6"
              >
                Request a Demo
              </Link>
            </div>
            <div className="md:w-2/5">
              <img
                src="https://doj8kefen1eet.cloudfront.net/images/marketing-progress-report.gif"
                alt="Screenshot"
                className="mx-auto rounded-md shadow-xl"
              />
            </div>
          </div>
        </section>
      </main>
    </div>
  )
}

export default Home
